import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, getDoc, query, orderBy } from "firebase/firestore";
import { db } from "./firebase";
import { eventToFirestore } from "../models/event";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "./collections";

/**
 * EventService
 * Centraliza as operações de banco de dados para o módulo de Eventos.
 * Coleção: tenants/{idTenant}/events
 */
const EventService = {
    /**
     * Busca todos os eventos de um tenant.
     */
    getAll: async (idTenant) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.EVENTS),
                orderBy("createdAt", "desc")
            );
            const querySnapshot = await getDocs(q);
            const events = [];
            querySnapshot.forEach((docSnap) => {
                events.push({ id: docSnap.id, ...docSnap.data() });
            });
            return events;
        } catch (error) {
            console.error("Erro ao listar eventos:", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza um evento.
     */
    save: async (formData, idTenant, authUser, existingEvents = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const eventData = eventToFirestore(formData, idTenant, authUser);

        try {
            if (formData.id) {
                // UPDATE
                const eventRef = doc(db, "tenants", idTenant, COLLECTIONS.EVENTS, formData.id);
                const oldEvent = existingEvents.find(e => e.id === formData.id) || {};
                await updateDoc(eventRef, eventData);

                // Auditoria
                const changes = AuditService.calculateDiff(oldEvent, eventData);
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.EVENTS,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: formData.id,
                    entityName: eventData.name,
                    description: `Atualizou o evento "${eventData.name}"`,
                    details: changes
                });

                return { id: formData.id, ...eventData };
            } else {
                // CREATE
                const newDocRef = await addDoc(collection(db, "tenants", idTenant, COLLECTIONS.EVENTS), eventData);

                // Auditoria
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.EVENTS,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: newDocRef.id,
                    entityName: eventData.name,
                    description: `Criou o evento "${eventData.name}"`
                });

                return { id: newDocRef.id, ...eventData };
            }
        } catch (error) {
            console.error("Erro no EventService.save:", error);
            throw error;
        }
    },

    /**
     * Remove um evento.
     */
    delete: async (eventId, idTenant, authUser, eventName) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTIONS.EVENTS, eventId));

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.EVENTS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: eventId,
                entityName: eventName,
                description: `Excluiu o evento "${eventName || eventId}"`
            });

            return true;
        } catch (error) {
            console.error("Erro no EventService.delete:", error);
            throw error;
        }
    },

    /**
     * Premia membros com a insígnia de um evento extra.
     * Grava na subcoleção awardedAchievements do membro (mesma do GamificationEngine).
     * @param {string} idTenant
     * @param {object} event - Dados do evento extra ({ id, name, badgeImageUrl })
     * @param {string[]} idMembers - IDs dos membros a premiar
     * @param {object} authUser - Usuário que está premiando
     */
    awardExtraEvent: async (idTenant, event, idMembers, authUser, xpReward = 0) => {
        if (!idTenant || !event?.id || !idMembers?.length) return;

        const { writeBatch, doc, serverTimestamp } = await import("firebase/firestore");
        const { default: XpService, XP_ACTION_TYPES } = await import("./xp_service");
        const batch = writeBatch(db);

        for (const idMember of idMembers) {
            const achRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember, COLLECTIONS.AWARDED_ACHIEVEMENTS, event.id);
            batch.set(achRef, {
                idAchievement: event.id,
                name: event.name,
                description: event.description || "",
                type: "extra",
                icon: "mdi-star",
                imageId: event.badgeImageUrl || null,
                rewardXp: Number(xpReward) || 0,
                threshold: 0,
                awardedAt: serverTimestamp(),
                awardedBy: {
                    uid: authUser?.uid || "system",
                    name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
                }
            });

            // Adiciona XP no mesmo batch, se houver
            if (Number(xpReward) > 0) {
                XpService.addXpInBatch(batch, idTenant, {
                    idMember,
                    xpAmount: Number(xpReward),
                    actionType: XP_ACTION_TYPES.ACHIEVEMENT_REWARD,
                    sourceId: event.id,
                    sourceName: event.name,
                    user: authUser
                });
            }
        }

        await batch.commit();

        // Auditoria — resolver nomes legíveis dos membros
        const memberNames = [];
        for (const idMember of idMembers) {
            const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);
            const memberSnap = await getDoc(memberRef);
            memberNames.push(memberSnap.exists() ? (memberSnap.data().fullName || memberSnap.data().name || idMember) : idMember);
        }

        AuditService.log({
            idTenant,
            user: authUser,
            module: AUDIT_MODULES.EVENTS,
            action: AUDIT_ACTIONS.UPDATE,
            entityId: event.id,
            entityName: event.name,
            description: `Premiou ${idMembers.length} membro(s) com a insígnia do evento "${event.name}": ${memberNames.join(", ")}`,
            details: { membros: memberNames, xp: Number(xpReward) || 0 }
        });
    },

    /**
     * Revoga a insígnia de um evento extra de um membro.
     */
    revokeExtraEvent: async (idTenant, eventId, idMember, authUser) => {
        if (!idTenant || !eventId || !idMember) return;

        const { doc, getDoc, writeBatch } = await import("firebase/firestore");
        const { default: XpService, XP_ACTION_TYPES } = await import("./xp_service");

        const achRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember, COLLECTIONS.AWARDED_ACHIEVEMENTS, eventId);
        const achSnap = await getDoc(achRef);
        
        if (!achSnap.exists()) return;

        const achData = achSnap.data();
        const batch = writeBatch(db);

        batch.delete(achRef);

        // Se a insígnia deu XP, removemos essa exata quantidade no mesmo batch
        if (achData.rewardXp && achData.rewardXp > 0) {
            XpService.addXpInBatch(batch, idTenant, {
                idMember,
                xpAmount: -achData.rewardXp,
                actionType: XP_ACTION_TYPES.MANUAL_ADJUSTMENT,
                sourceId: eventId,
                sourceName: `Revogação: ${achData.name || 'Evento'}`,
                description: "XP removido devido à revogação de insígnia de evento extra",
                user: authUser
            });
        }

        await batch.commit();
    },

    /**
     * Retorna a lista de membros que receberam uma insígnia de evento extra.
     * Útil para o relatório do evento, já que "extras" não possuem documentação de avaliação.
     */
    getExtraEventAwards: async (idTenant, eventId) => {
        if (!idTenant || !eventId) return [];
        try {
            const { collectionGroup, query, where, getDocs, getDoc } = await import("firebase/firestore");
            const q = query(
                collectionGroup(db, COLLECTIONS.AWARDED_ACHIEVEMENTS), 
                where("idAchievement", "==", eventId)
            );
            
            const snap = await getDocs(q);
            if (snap.empty) return [];

            const tenantPath = `tenants/${idTenant}/`;
            const validDocs = snap.docs.filter(d => d.ref.path.includes(tenantPath));

            const results = await Promise.all(validDocs.map(async d => {
                const memberRef = d.ref.parent.parent;
                const memberSnap = await getDoc(memberRef);
                const awardData = d.data();

                return {
                    id: d.id, // ID da conquista
                    idMember: memberRef.id,
                    memberName: memberSnap.exists() ? (memberSnap.data().name || "Desconhecido") : "Desconhecido",
                    employeeName: awardData.awardedBy?.name || "Sistema",
                    awardedAt: awardData.awardedAt?.toDate ? awardData.awardedAt.toDate() : new Date(),
                    rewardXp: awardData.rewardXp || 0,
                    status: "PASSED",
                    ...awardData
                };
            }));

            // Ordena alfabeticamente
            return results.sort((a, b) => (a.memberName || "").localeCompare(b.memberName || ""));
        } catch (error) {
            console.error("[EventService.getExtraEventAwards] Erro:", error);
            throw error;
        }
    }
};

export default EventService;
